/**
 * A manager's first card: what on the floor needs them now. Leads nobody owns, who is behind on
 * callbacks, drafts past due, deliveries that failed. Shown to supervisors and admins only.
 */
import { toUsDate, type FloorView } from "@tlcrm/shared";
import { Link } from "react-router";

import { age } from "../../lib/format";

export function Floor({ floor, now }: { floor: FloorView; now: number }) {
  const counts = [
    {
      n: floor.unownedLeads.length,
      label: "leads nobody owns",
      to: "/leads?status=new&owner=unassigned",
      stop: true,
    },
    {
      n: floor.overdueByAgent.reduce((a, r) => a + r.count, 0),
      label: "callbacks late",
      to: "/tasks?assignee=any",
      stop: true,
    },
    {
      n: floor.draftsOverdue.length,
      label: "drafts past due",
      to: "/policies?view=pending&scope=branch",
      stop: false,
    },
    { n: floor.unmatchedCalls, label: "unmatched calls", to: "/calls", stop: false },
    { n: floor.bsiFailed, label: "BSI deliveries failed", to: "/admin?tab=bsi", stop: true },
    {
      n: floor.settingsAwaiting,
      label: "settings awaiting an answer",
      to: "/admin?tab=settings",
      stop: false,
    },
  ];
  const clear = counts.every((c) => c.n === 0);
  return (
    <section className="card floor">
      <div className="card-head">
        <h3>The floor</h3>
        <span className="card-sub">What needs a manager now</span>
      </div>
      {clear ? (
        <div className="card-empty">
          <b>Nothing needs you.</b> Every lead has an owner and every callback is on time.
        </div>
      ) : (
        <>
          <div className="floor-counts">
            {counts.map((c) => (
              <Link
                key={c.label}
                to={c.to}
                className={`floor-count${c.n ? (c.stop ? " is-stop" : " is-warn") : ""}`}
              >
                <b className="mono">{c.n}</b>
                <span>{c.label}</span>
              </Link>
            ))}
          </div>
          <div className="floor-lists">
            {floor.unownedLeads.length > 0 && (
              <div>
                <span className="eyebrow">Waiting for an owner</span>
                <ul>
                  {floor.unownedLeads.slice(0, 5).map((l) => (
                    <li key={l.id}>
                      <Link to={`/leads/${l.id}`}>{l.name}</Link>
                      <span className="mono">
                        {l.state ?? "–"} · {age(now - Date.parse(l.receivedAt))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {floor.overdueByAgent.length > 0 && (
              <div>
                <span className="eyebrow">Late callbacks by agent</span>
                <ul>
                  {floor.overdueByAgent.slice(0, 5).map((r) => (
                    <li key={r.agent.id}>
                      <span>{r.agent.fullName}</span>
                      <span className="mono">
                        {r.count} · oldest {age(now - Date.parse(r.oldestDueAt))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {floor.draftsOverdue.length > 0 && (
              <div>
                <span className="eyebrow">Issued, draft past due</span>
                <ul>
                  {floor.draftsOverdue.slice(0, 5).map((d) => (
                    <li key={d.policyId}>
                      <Link to={`/policies/${d.policyId}`}>{d.name}</Link>
                      <span className="mono">
                        {d.owner.fullName.split(" ")[0]} · {toUsDate(d.effectiveDate)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );
}
